/**
 * Block 12 «Коммерческое предложение» — Zod-контракты для Экрана 2.12.10.
 *
 * **17-row spec table** — фиксированный список (ADR-0019 §Q9 + PRD §10
 * «Блок Состав», 17 канонических позиций):
 *   1. Столешница / 2. Фасады / 3. Фасад радиусный / 4. Фурнитура /
 *   5. Корзина выдвижная / 6. Складной фасад / 7. Мусорное ведро /
 *   8. Корзина для белья / 9. Штанга / 10. Пантограф / 11. Брючница /
 *   12. Гладильная доска / 13. Обувница / 14. Держатель для шланга пылесоса /
 *   15. Подсветка / 16. Витрина в алюминиевом профиле / 17. Металлические опоры.
 *
 * `specification.min(17).max(50)` — 17 базовых строк + до 33 manual «+ Добавить
 * строку» (cap 50). Каждая строка может иметь все 3 поля пустыми кроме `name`.
 *
 * **Auto-counter naming** — `'{Тип} №{N}'` (Кухня №1, Шкаф №2, ...). Server-side
 * incremented per (`userId`, `furnitureType`) — atomic transaction в Postgres
 * (ADR-0019 §Q9). Frontend в offline получает temporary имя `'{Тип} №(local)'`,
 * после Background Sync server replies actual `№N` → frontend обновляет IDB+UI.
 *
 * `furnitureType` — `z.string().min(1).max(100)` (PR-H7 ADR-0030 — relaxed от
 * `z.enum`). Accepts: (a) 8 ANKETA_TYPE_CARDS preset labels («Кухня»,
 * «Шкаф / Гардероб», «Санузел: тумба со столешницей», …), (b) legacy values
 * из pre-PR-H7 drafts («Гардероб», «Прихожая», «Санузел», «Балкон», …),
 * (c) custom strings через «Свой вариант» free-text input. `QUOTE_FURNITURE_TYPES`
 * vocabulary остаётся UI hint list для picker preset buttons, не runtime
 * validation enum. Это **разные** от Block 6 `FURNITURE_TYPES`
 * (configurator-vocabularies, 9 английских ключей для 3D-визуализации).
 *
 * References:
 *   - ADR-0030 (relaxation rationale + backward compat)
 *   - ADR-0019 §Q9 (17-row spec + auto-counter — counter keyed на string)
 *   - PRD `docs/prd/v4/10-block-12-calculator.md` Экран 2.12.10
 *   - data-model.md §12.2 (quotes table)
 *   - Vocabulary: `domain/quote-furniture-types-vocabulary.ts`
 */

import { z } from 'zod';

/**
 * PR-H7 (ADR-0030) — relaxed от `z.enum(QUOTE_FURNITURE_TYPES)`. См. ADR-0030 §1
 * для rationale. 100 chars cap покрывает longest preset label
 * («Санузел: тумба со столешницей» = 30 chars) + reasonable headroom для
 * custom user strings.
 */
export const QuoteFurnitureTypeSchema = z.string().min(1).max(100);

/** Стандартное название из 17-row spec (можно override через input). */
export const QuoteSpecificationRowSchema = z.object({
  name: z.string().min(1).max(200),
  type: z.string().max(200).optional(),
  brand: z.string().max(200).optional(),
});

/** Условия — гибкая структура (срок изготовления, аванс, гарантия, etc.). */
export const QuoteConditionSchema = z.object({
  label: z.string().min(1).max(100),
  value: z.string().min(1).max(500),
});

/** Контакты исполнителя (телефон, ФИО, мессенджер, ...). */
export const QuoteContactSchema = z.object({
  label: z.string().min(1).max(100),
  value: z.string().min(1).max(500),
});

/**
 * Quote create request body. `id` client-generated (offline-first), `projectId`
 * FK → projects.id. Cost — в тенге, целые, hard cap 1e9 (P-003 server-side
 * hard-cap на платежи; для quote это soft validation — реальный КП может быть
 * очень дорогим, но 1e9 ₸ = 2.2 млн $, разумная санитарная граница).
 */
export const QuoteCreateSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  furnitureType: QuoteFurnitureTypeSchema,
  specification: z.array(QuoteSpecificationRowSchema).min(17).max(50),
  cost: z.number().int().positive().max(1_000_000_000),
  conditions: z.array(QuoteConditionSchema).default([]),
  contacts: z.array(QuoteContactSchema).default([]),
  comments: z.string().max(5000).default(''),
  createdAt: z.string().datetime(),
});

/**
 * Server response — с auto-generated `name` (`'{Тип} №{N}'`) + `autoCounter` (N).
 * `pdfR2Key` — `nullable` (null если PDF ещё не загружен на R2 — offline state),
 * `.min(1)` при non-null (пустая строка → ошибка валидации).
 */
export const QuoteCreateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  autoCounter: z.number().int().positive(),
  projectId: z.string().uuid(),
  furnitureType: QuoteFurnitureTypeSchema,
  specification: z.array(QuoteSpecificationRowSchema),
  cost: z.number().int().positive(),
  conditions: z.array(QuoteConditionSchema),
  contacts: z.array(QuoteContactSchema),
  comments: z.string(),
  pdfR2Key: z.string().min(1).max(500).nullable(),
  createdAt: z.string().datetime(),
});

export type QuoteSpecificationRow = z.infer<typeof QuoteSpecificationRowSchema>;
export type QuoteCondition = z.infer<typeof QuoteConditionSchema>;
export type QuoteContact = z.infer<typeof QuoteContactSchema>;
export type QuoteCreate = z.infer<typeof QuoteCreateSchema>;
export type QuoteCreateResponse = z.infer<typeof QuoteCreateResponseSchema>;
